/**
 * Dashboard Layout Engine
 *
 * Scores and sorts modules based on user lifecycle stage and overlays
 */

import {
  DashboardContext,
  LifecycleStage,
  DashboardOverlay,
  ModuleId,
  ScoredModule,
} from './types';

/**
 * Score all modules based on current dashboard context
 * Higher scores appear first
 */
export function scoreModules(ctx: DashboardContext): ScoredModule[] {
  // Base scores for all modules
  const scores: Record<ModuleId, number> = {
    [ModuleId.HeroOverview]: 50,
    [ModuleId.OnboardingChecklist]: 0,
    [ModuleId.BridgeReview]: 0,
    [ModuleId.ConnectedExchanges]: 20,
    [ModuleId.RecentActivity]: 18,
    [ModuleId.TaxDashboard]: 16,
    [ModuleId.TaxTasks]: 0,
    [ModuleId.FilingCountdown]: 0,
    [ModuleId.AlertsCenter]: 0,
    [ModuleId.TopHoldings]: 15,
    [ModuleId.RecentTaxDisposals]: 14,
    [ModuleId.Tips]: 2,
    [ModuleId.UpgradeCTA]: 8,
  };

  // Lifecycle stage boosts
  switch (ctx.stage) {
    case LifecycleStage.NEW:
      scores[ModuleId.OnboardingChecklist] += 100;
      scores[ModuleId.HeroOverview] = 0; // Hide empty hero
      scores[ModuleId.Tips] += 20;
      break;

    case LifecycleStage.CONNECTING:
      scores[ModuleId.ConnectedExchanges] += 40;
      scores[ModuleId.Tips] += 15;
      break;

    case LifecycleStage.BOOTSTRAPPED:
      if (!ctx.metrics.hasTaxSettings) {
        scores[ModuleId.TaxDashboard] += 30;
      }
      break;

    case LifecycleStage.ACTIVE_HEAVY:
      scores[ModuleId.BridgeReview] += 15;
      scores[ModuleId.TaxTasks] += 12;
      break;

    case LifecycleStage.FILING_MODE:
      scores[ModuleId.FilingCountdown] += 60;
      scores[ModuleId.TaxTasks] += 40;
      scores[ModuleId.TaxDashboard] += 25;
      break;

    case LifecycleStage.POWER_USER:
      scores[ModuleId.Tips] = 0; // Hide tips for power users
      scores[ModuleId.RecentActivity] += 10;
      break;
  }

  // Overlay boosts (critical issues float to top)
  if (ctx.overlays.includes(DashboardOverlay.BROKEN_CONNECTIONS)) {
    scores[ModuleId.AlertsCenter] += 90;
    scores[ModuleId.ConnectedExchanges] += 30;
  }

  if (ctx.overlays.includes(DashboardOverlay.BRIDGE_REVIEW_NEEDED)) {
    scores[ModuleId.BridgeReview] += 80;
  }

  if (ctx.overlays.includes(DashboardOverlay.AUDIT_FLAGS)) {
    scores[ModuleId.AlertsCenter] += 70;
  }

  if (ctx.overlays.includes(DashboardOverlay.UNPRICED_ASSETS)) {
    scores[ModuleId.AlertsCenter] += 50;
  }

  if (ctx.overlays.includes(DashboardOverlay.DATA_DRIFT)) {
    scores[ModuleId.AlertsCenter] += 60;
  }

  // Usage-based upsell boost (works for both free and premium over-limit)
  if (ctx.metrics.isGrandfathered) {
    scores[ModuleId.UpgradeCTA] = 0;
  } else if (ctx.metrics.isOverPlanLimit && ctx.metrics.nextPlanSlug) {
    scores[ModuleId.UpgradeCTA] += 40; // High priority: over plan limit
  } else if (!ctx.isPremium) {
    const usage = ctx.metrics.freeTxnUsed / Math.max(1, ctx.metrics.freeTxnLimit);
    if (usage >= 0.7) {
      scores[ModuleId.UpgradeCTA] += 25;
    }
  } else {
    scores[ModuleId.UpgradeCTA] = 0; // Premium users under limit
  }

  // Convert to scored modules with layout hints
  const scoredModules: ScoredModule[] = Object.entries(scores)
    .filter(([_, score]) => score > 0) // Only include modules with positive scores
    .map(([id, score]) => ({
      id: id as ModuleId,
      score,
      layout: getModuleLayout(id as ModuleId, ctx),
    }))
    .sort((a, b) => b.score - a.score);

  return scoredModules;
}

/**
 * Get layout configuration for a module
 * Returns column span and section grouping
 */
function getModuleLayout(
  moduleId: ModuleId,
  ctx: DashboardContext
): ScoredModule['layout'] {
  const isPowerUser = ctx.stage === LifecycleStage.POWER_USER;

  // Define layout rules per module
  const layoutRules: Record<ModuleId, ScoredModule['layout']> = {
    [ModuleId.HeroOverview]: { colSpan: 12, section: 'hero' },
    [ModuleId.OnboardingChecklist]: { colSpan: 12, section: 'hero' },
    [ModuleId.FilingCountdown]: { colSpan: 12, section: 'hero' },

    [ModuleId.AlertsCenter]: { colSpan: 12, section: 'attention' },
    [ModuleId.BridgeReview]: { colSpan: 12, section: 'attention' },

    [ModuleId.ConnectedExchanges]: { colSpan: 6, section: 'main' },
    [ModuleId.RecentActivity]: { colSpan: isPowerUser ? 12 : 8, section: 'main' },
    [ModuleId.TopHoldings]: { colSpan: 5, section: 'main' },

    [ModuleId.TaxDashboard]: { colSpan: 7, section: 'tax' },
    [ModuleId.TaxTasks]: { colSpan: 6, section: 'tax' },
    [ModuleId.RecentTaxDisposals]: { colSpan: 8, section: 'tax' },

    [ModuleId.Tips]: { colSpan: 12, section: 'footer' },
    [ModuleId.UpgradeCTA]: { colSpan: 12, section: 'footer' },
  };

  return layoutRules[moduleId] || { colSpan: 6, section: 'main' };
}

// NOTE: detectLifecycleStage and detectOverlays have been moved to the server
// at /user/state endpoint. Use useUserState() hook to get stage and overlays.
